/**
 * Patient Insurance page — types (handoff §9). `/patients/:id` is one scrolling
 * page that verifies coverage from five named sources and always shows where
 * each answer came from.
 */

export type SourceKind = 'stedi' | 'historical_data' | 'payer_manual' | 'ai_call' | 'payer_portal';

/** The only valid source-marker labels (handoff §5). */
export const SOURCE_LABEL: Record<SourceKind, string> = {
  stedi: 'Stedi',
  historical_data: 'Historical data',
  payer_manual: 'Payer Manual',
  ai_call: 'AI Call',
  payer_portal: 'Payer Portal',
};

export type PlanOrder = 'primary' | 'secondary' | 'tertiary';
export type PlanNetwork = 'in' | 'out';

export interface InsurancePatient {
  id: string;
  name: string;
  initials: string;
  age: number;
  since: number;
  status: string[];
  phone: string;
  email: string;
  contactPreference: string;
  visits: number;
  language: string;
  nextVisit: { at: string; type: string; provider: string };
}

export interface Verification {
  lastAt: string;
  sources: number;
  nextCheck: string;
}

export interface InsurancePlan {
  id: string;
  order: PlanOrder;
  carrier: string;
  plan: string;
  type: string;
  network: PlanNetwork;
  memberId: string;
  group: string;
  subscriber: string;
  relationship: string;
  effective: string;
  payerId: string;
  phone: string;
  annualMax: number;
  used: number;
  remaining: number;
  resets: string;
  deductible?: { individual: number; met: number; family: number; familyMet: number };
  noDeductibleNote?: string;
  status: 'verified' | 'partly_verified';
  sourcesUsed?: number;
  callBooked?: string;
  /** Plain line shown when something is still unconfirmed on this plan. */
  unconfirmed?: { title: string; detail: string; sources: SourceKind[] };
  /** Rail card summary rows (visual pass §6): deductible, family, exams, cleanings. */
  railSummary?: { dt: string; dd: string; note?: string }[];
  /** A warning strip on the plan's rail card, e.g. Sunlife's "$0 left once…". */
  maxWarning?: string;
}

export interface BillingPreference {
  text: string;
  by: string;
  on: string;
}

export interface EstimateLine {
  code: string;
  label: string;
  fee: number;
  allowed: number;
  insurance: number;
  patient: number;
  note?: string;
}

export interface VisitEstimate {
  date: string;
  planSummary: string;
  lines: EstimateLine[];
  totals: { fee: number; allowed: number; insurance: number; patient: number; writeOff: number };
  maxAfterVisit: number;
  notes: { text: string; sources?: SourceKind[] }[];
}

export interface Opportunity {
  type: string;
  focus: boolean;
  title: string;
  /** Money shown: self-pay price, or insurance / patient split. */
  patientPays?: string;
  insurance?: number;
  patient?: number;
  why: string;
  sources: SourceKind[];
  /** When the insurance figure is capped, the one-line reason. */
  cappedNote?: string;
}

export interface NeedsAttentionItem {
  id: string;
  status: 'open' | 'resolved_today';
  title: string;
  detail: string;
  /** Section anchor id (or a drawer key) the View link points to. */
  viewTarget: string;
}

export interface Benefits {
  planId: string;
  remaining: number;
  annualMax: number;
  used: number;
  resets: string;
  deductibleLine?: string;
  familyDeductibleLine?: string;
  noDeductibleNote?: string;
  notCounted?: { text: string; sources: SourceKind[] };
  unconfirmed?: { title: string; detail: string; sources: SourceKind[] };
}

export interface CoverageRow {
  service: string;
  subLabel?: string;
  covered: string;
  rules: string;
  /** The rule resolved for THIS patient. */
  forPatient: { verdict: 'applies' | 'age' | 'excluded'; text: string };
  sources: SourceKind[];
  /** Fact id for the source markers' popover. */
  factId?: string;
  /** The office's own fee on a non-covered row (D5) — never payer policy. */
  officeFee?: number;
}

/** D1 — a dollar cap or a shared frequency: several codes draw on one pool. */
export interface Allowance {
  allowance: string;
  /** Sub-label under the allowance name, e.g. "Shared across both codes, not two each". */
  note?: string;
  drawsOn: string;
  limit: string;
  used: string;
  /** "unknown" when another office's claims could have drawn on it. */
  left: string;
  sources: SourceKind[];
}

/** D2 — a submission requirement for the team (not a benefit; not printed). */
export interface ClaimPrereq {
  requirement: string;
  severity: 'blocks' | 'date' | 'warns';
  appliesTo: string;
  whatElvaDoes: string;
  sources: SourceKind[];
}

export type RuleBadge = 'needs_clarification' | 'office_experience';

export interface PlanRuleRow {
  id: string;
  rule: string;
  meaning: string;
  howKnown: string;
  sources: SourceKind[];
  /** "Not yet known" when no source returned it. */
  notYetKnown?: boolean;
  factId?: string;
  /** D6 — imprecise payer wording, or an office pattern (confidence capped). */
  badge?: RuleBadge;
}

/** A row in the printed report's per-code "Services checked" table. */
export interface ReportServiceRow {
  code: string;
  service: string;
  planPays: string;
  frequency: string;
  lastDone: string;
  nextCovered: string;
  notes: string;
}

export interface FrequencyRow {
  service: string;
  rule: string;
  lastDone: string;
  nextCovered: string;
  status: 'covered' | 'not_yet' | 'cannot_compute' | 'no_limit';
  /** D7 — the clock starts on a different service; name it here. */
  fromService?: string;
  /** D4 — a standard-based limit that can't become a number. */
  badge?: 'needs_clarification';
}

export interface PlannedTreatmentRow {
  procedure: string;
  fee: number;
  insurance: number;
  patient: number;
  status: 'not_booked' | 'preauth_pending' | 'booked';
  statusDate?: string;
  /** False when a pending pre-auth means the share isn't decided yet. */
  insuranceKnown?: boolean;
  /** When the figure was capped, the one-line reason shown under the row. */
  cappedNote?: string;
  /** Expected decision date for a pending pre-auth. */
  decisionExpected?: string;
}

export interface PreAuthRow {
  procedure: string;
  reference: string;
  response: string;
  validUntil: string;
  status: 'pending' | 'approved' | 'used' | 'expired';
  statusDate?: string;
  source?: SourceKind;
}

export interface ClaimRow {
  serviceDate: string;
  claim: string;
  procedure: string;
  billed: number;
  allowed: number;
  insurance: number;
  patient: number;
  status: 'paid' | 'open' | 'denied';
  deniedReason?: string;
}

export interface SourceRow {
  n: number;
  kind: SourceKind;
  what: string;
  lastChecked: string;
  gave: string;
  status: 'done' | 'current' | 'partial' | 'scheduled';
  /** 'call' | 'portal' | 'rules' | none — what the last-column link opens. */
  link?: 'call' | 'portal' | 'rules';
  /** Citation for an appeal — trace / document id / §ref / claim ids. */
  citation?: string;
  /** A phone answer: verbal, not independently documented (weak evidence). */
  weak?: boolean;
}

export interface ReportMeta {
  id: string;
  snapshotId: string;
  preparedBy: string;
  practice: string;
  location: string;
}

export interface CallDetails {
  title: string;
  subtitle: string;
  numberCalled: string;
  when: string;
  length: string;
  why: string;
  rep: string;
  reference: string;
  confirmed: { label: string; value: string }[];
  recordingLength: string;
  transcript: { who: 'menu' | 'elva' | 'insurer' | 'hold'; text: string }[];
}

export interface PortalDetails {
  title: string;
  subtitle: string;
  portal: string;
  when: string;
  why: string;
  login: string;
  pages: number;
  facts: { text: string; page: string; newInfo?: boolean }[];
}

export interface FactSourceAnswer {
  kind: SourceKind;
  answer: string;
  tag: 'agrees' | 'no_answer' | 'na';
  link?: string;
  /** Citation for an appeal — trace / document id / §ref / claim ids. */
  citation?: string;
  /** When this source was captured. */
  at?: string;
  /** A phone answer: verbal, not independently documented. */
  weak?: boolean;
}

export interface FactPopoverData {
  id: string;
  title: string;
  uses: string;
  summary: string;
  sources: FactSourceAnswer[];
  usedIn: string;
}

export interface InsuranceFixture {
  today: string;
  report: ReportMeta;
  patient: InsurancePatient;
  verification: Verification;
  plans: InsurancePlan[];
  coordinationLine: string;
  billingPreference: BillingPreference;
  visitEstimate: VisitEstimate;
  opportunities: Opportunity[];
  hiddenBySettings: string[];
  needsAttention: NeedsAttentionItem[];
  benefits: Benefits[];
  allowances: Allowance[];
  coverage: Record<string, CoverageRow[]>;
  planRules: PlanRuleRow[];
  claimPrereqs: ClaimPrereq[];
  frequency: FrequencyRow[];
  plannedTreatment: { rows: PlannedTreatmentRow[]; totalLabel: string; footnote?: string };
  /** Per-code services table for the printed eligibility report (PrintView.dc.html). */
  reportServices: ReportServiceRow[];
  preAuths: PreAuthRow[];
  claims: ClaimRow[];
  sources: Record<string, SourceRow[]>;
  call: CallDetails;
  portal: PortalDetails;
  facts: Record<string, FactPopoverData>;
}
